/**
 * Card #2909 — how a file arrived, in the words a staffer reads ("Arrived via" on the file page,
 * "arrived via" on a board row).
 *
 * <p>The status view's `opened_via` is `jobs.triggered_by`, passed through verbatim. The platform
 * writes exactly two kinds of value there:</p>
 * <ul>
 *   <li><b>A declared label</b> — a webhook trigger's `trigger.label` (e.g. "GoProposal"). It is
 *       already a readable name the workflow author chose, so it is shown exactly as written.</li>
 *   <li><b>A platform constant</b> — `platform:scheduler` (`ActorPrefixes.PlatformScheduler`, the
 *       scheduler's actor, stamped on every timer-started job). That is an internal value and is
 *       never shown (L-97, form rule 7): it reads as "Scheduled run". Any other `platform:` value a
 *       later engine might write reads as a plain generic phrase rather than leaking the code.</li>
 * </ul>
 * <p>Manual and agent starts leave the column NULL; so does a webhook slot that declares no label.
 * That is `null` here, and each caller keeps its own "not recorded" voice for it.</p>
 */

/** The family every platform-written `triggered_by` constant belongs to. */
const PLATFORM_PREFIX = "platform:";

/** Each platform constant written to `jobs.triggered_by`, in plain words. */
export const PLATFORM_START_WORDS: ReadonlyMap<string, string> = new Map([
  ["platform:scheduler", "Scheduled run"],
]);

/** A platform constant this portal has no words for yet — still never the raw code. */
export const UNKNOWN_PLATFORM_START = "Started by the platform";

/**
 * The file's source as a person reads it, or `null` when the file holds none (the caller says
 * "not recorded"). A declared label comes back verbatim; a platform constant comes back in words.
 */
export function arrivedVia(openedVia: string | null | undefined): string | null {
  if (openedVia === null || openedVia === undefined) return null;
  const value = openedVia.trim();
  if (value === "") return null;
  const words = PLATFORM_START_WORDS.get(value);
  if (words !== undefined) return words;
  if (value.startsWith(PLATFORM_PREFIX)) return UNKNOWN_PLATFORM_START;
  return openedVia;
}
